import { Button, IconButton, Stack, Typography } from '@mui/material'
import { format, isBefore } from 'date-fns'
import { find } from 'lodash'
import { FC, MouseEvent, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ReplyScheduleDTO } from '../../../../api/gen/base'
import { useInvalidateReplyScheduleCalendarQuery } from '../../../../api/hooks/useInvalidateReplyScheduleCalendarQuery'
import ArrowRightIcon from '../../../../assets/icons/ArrowRight.svg?react'
import PenIcon from '../../../../assets/icons/Pen.svg?react'
import TrashIcon from '../../../../assets/icons/Trash.svg?react'
import { ModalDialogWrapper } from '../../../../components/ModalDialogWrapper'
import { REQUEST_ROUTE } from '../../../../constants/routing.constants'
import { RegularRequestDeleteDialog } from '../../../../dialogs/RegularRequestDeleteDialog'
import {
    RegularRequestEditDialog,
    RegularRequestEditDialogData,
} from '../../../../dialogs/RegularRequestEditDialog'
import { useWrapperStore } from '../../../../store/storeWrapper'
import {
    getRegularRequestFormFilesFromReplyContent,
    getTextContentFromReplyContent,
} from '../../../../utils/regularRequests'

type Props = {
    open: boolean
    onClose: VoidFunction
    data?: ReplyScheduleDTO
}

export const RegularRequestInfoDialog: FC<Props> = ({ open, onClose, data }) => {
    const navigate = useNavigate()

    const invalidateRequests = useInvalidateReplyScheduleCalendarQuery()

    const { projectListFull, setCurrentAgentAndAssistantIdDialog, fetchModelDetail } =
        useWrapperStore((state) => state)

    const [regularRequestEditDialogData, setRegularRequestEditDialogData] =
        useState<RegularRequestEditDialogData | null>(null)
    const [regularRequestDeleteDialogData, setRegularRequestDeleteDialogData] = useState<
        number | null
    >(null)

    const projectName = useMemo(() => {
        const projectDetail = find(projectListFull, (item) => {
            return item.id === data?.workplace_id
        })

        return projectDetail?.title ?? 'Без проекта'
    }, [projectListFull, data])

    const textContent = getTextContentFromReplyContent(data?.reply.content ?? [])
    const filesContent = getRegularRequestFormFilesFromReplyContent(data?.reply.content ?? [])

    const handleOpenEditRegularRequestDialog = () => {
        if (!data) {
            return
        }

        const {
            id,
            reply: { thread_id, assistant_id, model_slug },
            timing,
        } = data

        const currentAgentNameInDialog = model_slug ? model_slug : 'AUTO'

        setCurrentAgentAndAssistantIdDialog({
            agent: currentAgentNameInDialog,
            assistantId: assistant_id ?? undefined,
        })
        fetchModelDetail(currentAgentNameInDialog, assistant_id ?? undefined)

        setRegularRequestEditDialogData({
            text: textContent?.text ?? '',
            startDateTime: timing.send_time,
            repeatType: timing.repeat,
            repeatDays: timing.weekdays ?? undefined,
            files: filesContent,
            threadId: thread_id,
            replyScheduleId: id,
        })

        onClose?.()
    }

    const handleOpenDeleteRegularRequestDialog = () => {
        setRegularRequestDeleteDialogData(data?.id)
        onClose?.()
    }

    const handleCloseEditRegularRequestDialog = () => {
        setRegularRequestEditDialogData(null)
    }

    const handleCloseDeleteRegularRequestDialog = () => {
        setRegularRequestDeleteDialogData(null)
    }

    const handleRefetchDataOnSuccess = () => {
        invalidateRequests()
    }

    const handleOpenThread = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation()

        navigate(`${REQUEST_ROUTE}/${data?.reply.thread_id}`)
        onClose?.()
    }

    const repeatInfo = useMemo(() => {
        if (data?.timing.repeat === 'EVERYDAY') {
            return 'Ежедневно'
        }

        if (data?.timing.repeat === 'WEEK_DAYS') {
            return 'По дням недели'
        }

        return 'Не повторять'
    }, [data])

    const canEdit = !!data && isBefore(new Date(), data.timing.send_time)

    return (
        <>
            <RegularRequestEditDialog
                open={!!regularRequestEditDialogData && !regularRequestDeleteDialogData}
                initialData={regularRequestEditDialogData}
                onClose={handleCloseEditRegularRequestDialog}
                onDelete={handleOpenDeleteRegularRequestDialog}
                onSubmit={handleRefetchDataOnSuccess}
            />
            <RegularRequestDeleteDialog
                open={!!regularRequestDeleteDialogData}
                replyScheduleId={regularRequestDeleteDialogData}
                onClose={handleCloseDeleteRegularRequestDialog}
                onSuccess={() => {
                    handleRefetchDataOnSuccess()
                    handleCloseEditRegularRequestDialog()
                }}
            />
            <ModalDialogWrapper
                open={open}
                handleClose={onClose}
                title={
                    <Typography
                        variant="h5"
                        sx={{
                            width: '100%',
                            display: '-webkit-box',
                            WebkitLineClamp: '3',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                        {textContent?.text ?? ''}
                    </Typography>
                }
                footerActions={
                    canEdit ? (
                        <>
                            <IconButton
                                onClick={handleOpenDeleteRegularRequestDialog}
                                sx={{
                                    border: '1px solid #E9EAED',
                                    width: '40px',
                                    height: '40px',
                                }}>
                                <TrashIcon color="#E82C1F" />
                            </IconButton>
                            <IconButton
                                onClick={handleOpenEditRegularRequestDialog}
                                sx={{
                                    border: '1px solid #E9EAED',
                                    width: '40px',
                                    height: '40px',
                                }}>
                                <PenIcon color="#121317" />
                            </IconButton>
                            <Button
                                variant="contained"
                                endIcon={<ArrowRightIcon color="#fff" />}
                                onClick={handleOpenThread}
                                sx={{
                                    width: '100%',
                                    '.MuiButton-endIcon': {
                                        marginLeft: 0,
                                    },
                                }}>
                                Перейти к запросу
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="contained"
                            endIcon={<ArrowRightIcon color="#fff" />}
                            onClick={handleOpenThread}
                            sx={{
                                width: '100%',
                                '.MuiButton-endIcon': {
                                    marginLeft: 0,
                                },
                            }}>
                            Перейти к запросу
                        </Button>
                    )
                }>
                <Stack direction="column" gap={1}>
                    <Stack direction="row" alignItems="center" gap="4px">
                        <Typography variant="body2" color="#5A5F72">
                            Проект:
                        </Typography>
                        <Typography
                            variant="body2"
                            color="#181B25"
                            sx={{
                                width: '100%',
                                display: '-webkit-box',
                                WebkitLineClamp: '1',
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}>
                            {projectName}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap="4px">
                        <Typography variant="body2" color="#5A5F72">
                            Дата начала:
                        </Typography>
                        <Typography variant="body2" color="#181B25">
                            {data?.timing?.send_time
                                ? format(data.timing.send_time, 'dd.MM.yyyy, HH:mm')
                                : ''}
                        </Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" gap="4px">
                        <Typography variant="body2" color="#5A5F72">
                            Повтор:
                        </Typography>
                        <Typography variant="body2" color="#181B25">
                            {repeatInfo}
                        </Typography>
                    </Stack>
                    {!!filesContent.length && (
                        <Stack direction="row" alignItems="center" gap="4px">
                            <Typography variant="body2" color="#5A5F72">
                                Вложения:
                            </Typography>
                            <Typography variant="body2" color="#181B25">
                                {filesContent.length}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </ModalDialogWrapper>
        </>
    )
}
